import React from 'react';
import { DashboardLayout, WidgetId, Zone } from './dashboardLayout';
import { WidgetFrame, ZoneArea } from './WidgetFrame';

/**
 * Das Dashboard am PC, aus dem Layout gezeichnet: `top` ueber die ganze
 * Breite, dann Haupt- und Seitenspalte, dann `bottom`. Ist eine der beiden
 * Spalten leer, nimmt die andere die volle Breite (eine volle Seitenspalte
 * legt ihre Bausteine in ein Raster). Im Anpassen-Modus sind alle vier Zonen
 * sichtbar — auch leere, als Ablage.
 */

interface Props {
    layout: DashboardLayout;
    editing: boolean;
    dragging: WidgetId | null;
    /** Zeichnet einen Baustein mit allen Daten; null, wenn er gerade nichts zu zeigen hat. */
    renderWidget: (id: WidgetId) => React.ReactNode;
    /** Ziel fuer Kachel- und Glockenklick (nur in der Ansicht gesetzt). */
    widgetRef: (id: WidgetId, el: HTMLElement | null) => void;
    onMove: (id: WidgetId, zone: Zone, index: number) => void;
    onNudge: (id: WidgetId, delta: -1 | 1) => void;
    onHide: (id: WidgetId) => void;
    onDragStart: (id: WidgetId) => void;
    onDragEnd: () => void;
}

const EmptyNote: React.FC = () => (
    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-4 text-[11px] text-slate-500">
        Zeigt gerade nichts an — der Platz bleibt reserviert.
    </div>
);

export const DashboardGrid: React.FC<Props> = ({ layout, editing, dragging, renderWidget, widgetRef, onMove, onNudge, onHide, onDragStart, onDragEnd }) => {
    const z = layout.zones;

    if (editing) {
        const zone = (name: Zone) => {
            const ids = z[name];
            return (
                <ZoneArea zone={name} count={ids.length} dragging={dragging} onDrop={onMove}>
                    {ids.map((id, i) => (
                        <WidgetFrame
                            key={id}
                            id={id}
                            zone={name}
                            canUp={i > 0}
                            canDown={i < ids.length - 1}
                            dragging={dragging === id}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            onNudge={onNudge}
                            onMoveToZone={(wid, target) => onMove(wid, target, z[target].length)}
                            onHide={onHide}
                        >
                            {renderWidget(id) ?? <EmptyNote />}
                        </WidgetFrame>
                    ))}
                </ZoneArea>
            );
        };
        return (
            <div className="space-y-6 pt-2">
                {zone('top')}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    <div className="lg:col-span-7 xl:col-span-8 min-w-0">{zone('main')}</div>
                    <div className="lg:col-span-5 xl:col-span-4 min-w-0">{zone('side')}</div>
                </div>
                {zone('bottom')}
            </div>
        );
    }

    // Ansicht: Bausteine ohne Inhalt (z. B. Vertretungen ohne Vertretung)
    // fallen samt Huelle weg, sonst bliebe ihr Abstand stehen.
    const draw = (ids: ReadonlyArray<WidgetId>): React.ReactNode[] =>
        ids.flatMap(id => {
            const node = renderWidget(id);
            if (node === null || node === undefined || node === false) return [];
            return [
                <div key={id} ref={el => widgetRef(id, el)} data-widget={id} className="min-w-0 scroll-mt-3">
                    {node}
                </div>,
            ];
        });
    const top = draw(z.top);
    const main = draw(z.main);
    const side = draw(z.side);
    const bottom = draw(z.bottom);
    const hasMain = main.length > 0;
    const hasSide = side.length > 0;

    return (
        <>
            {top.length > 0 && <div className="space-y-4">{top}</div>}
            {(hasMain || hasSide) && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                    {hasMain && (
                        <div className={`${hasSide ? 'lg:col-span-7 xl:col-span-8' : 'lg:col-span-12'} space-y-4 min-w-0`}>{main}</div>
                    )}
                    {hasSide && hasMain && (
                        <div className="lg:col-span-5 xl:col-span-4 space-y-4 min-w-0">{side}</div>
                    )}
                    {hasSide && !hasMain && (
                        <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start min-w-0">{side}</div>
                    )}
                </div>
            )}
            {bottom.length > 0 && <div className="space-y-4">{bottom}</div>}
        </>
    );
};
